import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface TagItem {
  name: string
  count: number
}

export interface PaginatedTagCloudProps {
  tags: TagItem[]
  pageSize?: number
  label: string
  tagPrefix: string
  onTagSelect: (tagPrefix: string, tagName: string) => void
  variant: 'primary' | 'neutral'
}

const DEFAULT_PAGE_SIZE = 10

const variantStyles = {
  primary: {
    chip: 'bg-surface-soft text-muted hover:bg-surface-card',
    count: 'text-muted/60',
  },
  neutral: {
    chip: 'bg-surface-soft text-muted hover:bg-surface-card',
    count: 'text-muted/60',
  },
}

export function PaginatedTagCloud({
  tags,
  pageSize = DEFAULT_PAGE_SIZE,
  label,
  tagPrefix,
  onTagSelect,
  variant,
}: PaginatedTagCloudProps) {
  const [visibleCount, setVisibleCount] = useState(pageSize)

  if (tags.length === 0) return null

  const visibleTags = tags.slice(0, visibleCount)
  const hasMore = tags.length > visibleCount
  const remaining = tags.length - visibleCount
  const styles = variantStyles[variant]

  return (
    <div>
      <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mb-2">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {visibleTags.map(tag => (
          <button
            key={`${tagPrefix}-${tag.name}`}
            onClick={() => onTagSelect(tagPrefix, tag.name)}
            className={cn(
              'flex items-center gap-1 px-4 py-1.5 rounded-full',
              'text-[12px] font-bold tracking-[0.05em] uppercase transition-colors cursor-pointer',
              'min-h-[44px]',
              styles.chip,
            )}
          >
            <span>{tag.name}</span>
            <span className={cn('text-xs', styles.count)}>{tag.count}</span>
          </button>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setVisibleCount(prev => prev + pageSize)}
          className="mt-2 flex items-center gap-1 px-3 py-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors cursor-pointer min-h-[44px]"
          aria-label={`Show ${remaining} more ${label.toLowerCase()}`}
        >
          <span className="material-symbols-outlined text-[18px]">expand_more</span>
          <span>+{remaining} more</span>
        </button>
      )}
    </div>
  )
}
